import React, { useEffect } from 'react';

import { Icon } from 'components/UI';

import {
  Typography,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Drawer,
  Dialog,
  Stack,
  Box,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import { useAnimatedHeight } from '@repo/ui';

import { useDesktopSheet } from 'components/UI/DesktopSheetContext';

interface DrawerPopupProps {
  title: string;
  child: any;
  anchor?: 'top' | 'right' | 'bottom' | 'left' | undefined;
  handleOpen: boolean;
  handleClose: () => void;
  rightActionLabel?: string;
  rightActionOnClick?: () => void;
  paperSx?: SxProps<Theme>;
  hideHeader?: boolean;
  hideHandle?: boolean;
  contentHeight?: string | number;
  backgroundColor?: string;
  titleUppercase?: boolean;
  headerRight?: React.ReactNode;
  /**
   * Slot BEFORE the title — in practice a back arrow when the sheet is showing a
   * STEP rather than its root content (working-conventions §52: a level-2 sheet
   * advances in place instead of opening a third drawer, and the step's affordance
   * is a back arrow in this same header, not a second dismissal).
   *
   * Symmetric with `headerRight`, and deliberately a slot rather than an `onBack`
   * boolean: the sheet does not own the step machine, its caller does.
   */
  headerLeft?: React.ReactNode;
  titleSx?: any;
  fullScreen?: boolean;
  contentSx?: any;
  disableBackdropClick?: boolean;
  disableSwipeToClose?: boolean;
}

export const DrawerPopup = ({
  title,
  handleOpen,
  anchor = 'bottom',
  handleClose,
  child,
  rightActionLabel,
  rightActionOnClick,
  paperSx,
  hideHeader = false,
  hideHandle = false,
  contentHeight,
  backgroundColor,
  titleUppercase = false,
  headerRight,
  headerLeft,
  titleSx,
  // VERA-842: content-height is now the DEFAULT for bottom sheets (was
  // `true`, so every drawer that didn't opt out rendered at 100vh — the Log
  // out confirm filled the page for two lines of text). Callers that genuinely
  // need a full-height sheet opt in with `fullScreen`.
  fullScreen = false,
  contentSx,
  disableBackdropClick = false,
  disableSwipeToClose = false,
}: DrawerPopupProps) => {
  const theme = useTheme();
  const { palette } = theme;

  /*
   * VERA-315 — the desktop presentation, opted into per subtree (see
   * `DesktopSheetContext`). A bottom sheet is the right control on a phone and the
   * wrong one on a 1280-wide window, where it spans the whole width and arrives from
   * an edge a mouse never travels to.
   *
   * A DIALOG, not a re-positioned Drawer. Centring a Drawer's Paper needs a
   * `transform`, and MUI's `Slide` writes `transform` inline on that same element for
   * the whole transition (and leaves `transform: none` on it when entered) — so any
   * centring transform either loses to the inline value or has to be `!important`,
   * which then freezes the animation. `Dialog` centres natively, fades, and keeps
   * backdrop-click and Escape. The CONTENT is byte-identical: both branches render
   * the same `drawerContent`.
   *
   * Gated on `anchor === 'bottom'` so a deliberate side panel (the certificates
   * filter's desktop `anchor='right'`) is left alone.
   */
  const desktopSheetEnabled = useDesktopSheet();
  const isDesktopWidth = useMediaQuery(theme.breakpoints.up('md'));
  const asDialog = desktopSheetEnabled && isDesktopWidth && anchor === 'bottom' && !fullScreen;

  const [openDrawer, setOpenDrawer] = React.useState(false);

  /*
   * VERA-315 — animate the sheet's height when its CONTENT changes size, instead of
   * letting it snap. User directive: "all menus or modals should animate expand in
   * size rather than flash when size changes — same as what is on platform".
   *
   * This is `@repo/ui`'s `useAnimatedHeight`, the SAME hook the platform's shared
   * `Modal` uses (packages/ui/components/feedback/Modal/Modal.tsx:55) — not a second
   * mechanism (working-conventions §47: improve/reuse layer 1, never rebuild sideways).
   * Its docstring carries the two constraints that matter here:
   *
   *  - `height: auto` CANNOT be transitioned in CSS, so the resize has to be MEASURED.
   *    The hook does that with a `ResizeObserver` + a WAAPI `el.animate()` over the
   *    measured from/to, which releases the height back to `auto` when it finishes —
   *    so the sheet keeps sizing itself from content afterwards.
   *  - `prefers-reduced-motion` is gated in JS, not in a media query, because MUI's
   *    transition components write INLINE styles a media query cannot override. The
   *    hook returns before creating the observer when the user asks for reduced
   *    motion, so the resize is instant for them.
   *
   * It is a CALLBACK ref, and that is load-bearing rather than stylistic: this hook is
   * called while rendering a CLOSED drawer, which mounts no Paper at all, so a
   * `useRef` + `useEffect` pair would find `null` and never re-run. Same reason the
   * platform Modal uses one.
   *
   * Placed on the PAPER (the element whose height the user sees), applied identically
   * to all three presentations below so a sheet does not animate on a phone and snap
   * in its desktop dialog form.
   *
   * DISABLED for `fullScreen`, whose Paper is locked to exactly `100vh` with
   * `overflow: hidden` — its height cannot change, so an observer there is dead weight,
   * and the hook's temporary `overflow: hidden` has nothing to add to a Paper that
   * already relies on that lock (see `resolvedPaperSx`).
   */
  const animatedPaperRef = useAnimatedHeight<HTMLDivElement>(!fullScreen);

  useEffect(() => {
    setOpenDrawer(handleOpen);
  }, [handleOpen]);

  const closeDrawer = () => {
    setOpenDrawer(false);
    handleClose();
  };

  const handleDrawerClose = (event: {}, reason?: 'backdropClick' | 'escapeKeyDown') => {
    // Prevent closing on backdrop click if disabled
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    closeDrawer();
  };

  const drawerContent = (
    <Box
      sx={{
        background: backgroundColor ?? theme.palette.common.white,
        minWidth: 320,
        maxWidth: '100vw',
        height: contentHeight ?? (anchor === 'bottom' ? (fullScreen ? '100vh' : 'auto') : '100%'),
        display: 'flex',
        flexDirection: 'column',
        // In the dialog branch the Paper owns the radius on all four corners, so the
        // content must not draw a second, smaller one inside it.
        borderTopLeftRadius:
          !asDialog && !fullScreen && anchor === 'bottom' ? `${theme.shape.borderRadius}px` : 0,
        borderTopRightRadius:
          !asDialog && !fullScreen && anchor === 'bottom' ? `${theme.shape.borderRadius}px` : 0,
      }}
    >
      {/*
        VERA-304: the drag handle and the header are ONE sticky unit, wrapped
        together, rather than the header alone being sticky with the handle as
        unstickied content above it. Previously the handle sat in normal flow
        ABOVE the sticky header, so as soon as the user scrolled past the
        handle's own height it scrolled out of view while the header snapped
        to `top: 0` -- a visible jump/shrink in the effective header height at
        the moment scrolling started ("sticky headers strangely slide up a bit
        to hide the dragger"). Wrapping both in one sticky container keeps the
        combined handle+header block at a CONSTANT height and position for the
        whole scroll, with the handle staying visibly pinned above the header
        instead of disappearing.

        VERA-291: sticky only for short (non-fullScreen) bottom sheets, which
        scroll at the Paper level once content exceeds the `maxHeight: 90vh`
        cap (MUI's default `.MuiDrawer-paper` is `overflowY: auto`) -- without
        `sticky` the header would scroll away with the content there. The
        background match is required so scrolled content passes BEHIND the
        header instead of showing through it.

        A `fullScreen` drawer (e.g. the legal-document drawer) must NOT get
        this: it has its own descendant scroll region (see
        LegalDocumentDrawer's `overflowY: 'auto'` Box) and relies on
        `resolvedPaperSx` below locking the Paper itself to
        `overflow: 'hidden'` so it can never become a SECOND, independent
        scroll container. Applying `position: sticky` to this wrapper
        regardless was harmless on paper but, if the Paper ever became
        scrollable in its own right (a fraction of a pixel of height drift is
        enough -- dynamic mobile viewport units, border rounding), the header
        would stick to the Paper's scroll while the document's own inner
        scroll region moved independently, dragging the document's first
        lines up underneath the header. Locking the Paper's overflow removes
        that failure mode outright; scoping `sticky` to the case that
        actually needs it (Paper IS the intended scroll owner) keeps the two
        mechanisms from ever fighting each other again.
      */}
      <Box
        sx={{
          flexShrink: 0,
          ...(!fullScreen && {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: backgroundColor ?? theme.palette.common.white,
          }),
        }}
      >
        {/* The grab handle is a SWIPE affordance. A centred dialog is not swiped, so
            drawing one there advertises a gesture that does nothing. */}
        {anchor === 'bottom' && !hideHandle && !fullScreen && !asDialog && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: 1,
              pb: 0.5,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: palette.grey[400],
                borderRadius: `${theme.shape.borderRadius / 4}px`,
              }}
            />
          </Box>
        )}
        {!hideHeader && (
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            sx={{
              p: '16px',
              // grey[300] is NOT customised in this app's palette, so it falls back
              // to MUI's default #e0e0e0 — a hairline too pale to read as a header
              // separator on a phone (reported as "do not see it"). grey[400] gives
              // the bar a visible edge without turning it into a heavy rule.
              borderBottom: `1px solid ${palette.grey[400]}`,
            }}
          >
            {/* The title and any left slot travel together, so a back arrow sits
                beside the title rather than being pushed to the far edge by the
                header's space-between. */}
            <Stack direction='row' alignItems='center' spacing={1.5}>
              {headerLeft}
              {/* VERA-315: `h3`, not `h1`. This has always RENDERED at 20/500; the
                  variant was `h1` — the 32px display step — cut down by more than a
                  third inline, so every sheet title in the app was a page-title
                  variant in disguise and carried h1's display leading (1.2) and
                  display tracking (-0.02em) at a size neither was drawn for.
                  `h3` IS this scale's 20px step (`font.heading`), so the size now
                  comes from the scale and only the weight is stated: a sheet title
                  sits one notch lighter than a page heading (500 vs 600) because
                  it names a temporary surface, not the page you are on.
                  `h3` also keeps the tenant's TITLE font and the `text.heading`
                  colour hook (settings/mui/MuiTypography.ts styles h1–h4 alike), so
                  nothing white-label-facing changes by moving between them. */}
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 500,
                  color: palette.text.primary,
                  textTransform: titleUppercase ? 'uppercase' : 'none',
                  ...titleSx,
                }}
              >
                {title}
              </Typography>
            </Stack>
            {rightActionLabel ? (
              <Typography
                onClick={rightActionOnClick ?? closeDrawer}
                sx={{ cursor: 'pointer', color: palette.text.primary, fontSize: '16px' }}
              >
                {rightActionLabel}
              </Typography>
            ) : headerRight ? (
              headerRight
            ) : (
              <Icon name='close' onClick={closeDrawer} />
            )}
          </Stack>
        )}
      </Box>
      <Box
        py={3}
        px={anchor === 'bottom' ? 2 : 0}
        sx={{
          flex: fullScreen ? 1 : 'unset',
          // VERA-291: without `minHeight: 0`, a flex item defaults to
          // `min-height: auto` — sized to its content, not shrinkable to the
          // space `flex: 1` assigns it. That broke the scroll chain for long
          // content (e.g. the Terms/Privacy legal documents): this Box grew
          // taller than the fullScreen drawer's 100vh instead of yielding to
          // its `flex: 1` share, so the content overflowed past the viewport
          // with no scroll container ever catching it — the end of the
          // document was simply unreachable. `minHeight: 0` lets this Box
          // actually shrink to its allotted height so a descendant's
          // `overflowY: 'auto'` (see LegalDocumentDrawer) can do its job.
          minHeight: fullScreen ? 0 : 'unset',
          ...contentSx,
        }}
      >
        {child}
      </Box>
    </Box>
  );

  // VERA-842: a bottom sheet must be the height of its CONTENT, never the full
  // page. The inner Box was already `height: auto`, but the Drawer's own Paper
  // had no constraint, so it could stretch to the viewport. Constrain the Paper
  // itself (with a sane cap so long content scrolls instead of overflowing) so
  // EVERY DrawerPopup gets this — callers can still override via `paperSx`.
  //
  // VERA-291: a `fullScreen` drawer needs the opposite constraint. Its
  // content `Box` is already exactly `100vh` and owns a descendant
  // `overflowY: 'auto'` region for the actual scrolling (see
  // LegalDocumentDrawer) — the Paper itself must never ALSO become
  // scrollable. MUI's `.MuiDrawer-paper` defaults to `overflowY: auto` with
  // no height cap, so without an explicit lock here any sub-pixel height
  // drift (mobile dynamic viewport units, border rounding) can make the
  // Paper itself scrollable — a second, independent scroll container
  // stacked on top of the first. When that happens, the sticky/fixed header
  // sticks to the Paper's own scroll position while the document's inner
  // scroll region moves independently underneath it, so the document's
  // opening lines render UNDER the header instead of below it. Locking the
  // Paper to `height: 100vh` + `overflow: 'hidden'` makes the header a true
  // non-scrolling flex sibling of the scroll region, with no pixel offset
  // required — the only thing that scrolls is the inner region itself.
  /*
   * The Paper's top corners must be the SAME radius the content Box above draws,
   * because the Paper is what CLIPS that content.
   *
   * They disagreed. The Paper's radius came from a hard-coded
   * `.MuiDrawer-paperAnchorBottom: '8px 8px 0 0'` (`settings/mui/MuiPaper.ts`),
   * while the content Box reads `theme.shape.borderRadius` — which at runtime is
   * the TENANT's Studio `border_radius` (`settings/mui/createMuiTheme.ts`), not a
   * constant. Measured on the current tenant: paper 8px, content 10px, so the
   * content was drawing a rounder corner than its own clip. Invisible today only
   * because both happen to be white — but `backgroundColor` is a prop on this
   * component, and the moment a sheet sets one, the Paper's white corner shows
   * through the content's larger curve. It also means a brand that picks 0
   * ("Sharp") or 24 gets a sheet whose two corner radii disagree by that much.
   *
   * Stating it from the token here makes them agree BY CONSTRUCTION, and keeps
   * the tenant's brand radius as the single source (VERA-837's intent) rather
   * than half the sheet honouring it and half not.
   */
  const resolvedPaperSx =
    anchor === 'bottom' && !fullScreen
      ? {
          height: 'auto',
          maxHeight: '90vh',
          borderTopLeftRadius: `${theme.shape.borderRadius}px`,
          borderTopRightRadius: `${theme.shape.borderRadius}px`,
          ...(paperSx as object),
        }
      : fullScreen
        ? { height: '100vh', overflow: 'hidden', ...(paperSx as object) }
        : paperSx;

  /*
   * VERA-315 — a `fullScreen` drawer must sit ABOVE the modal band, not in the
   * drawer band, or the desktop flip buries it.
   *
   * MUI's z-index tokens are STATIC per component type — `zIndex.drawer` is 1200 and
   * `zIndex.modal` is 1300 — and the ModalManager does NOT bump them per open modal.
   * While every sheet in this app was a `Drawer`, a stack of two shared one band and
   * DOM order decided the winner, which happened to be correct. Making bottom sheets
   * render as `Dialog` at md+ split the stack across two bands, and the measured
   * result was the legal-document drawer rendering BEHIND the phone-verification
   * sheet that opened it: at 375 both were drawers at 1200 (document on top, right);
   * at 1280 the parent became a Dialog at 1300 and the document stayed at 1200, so a
   * full-window Terms takeover was covered by the 520px dialog it was launched from.
   *
   * Raised for `fullScreen` only, at EVERY width — not gated on `isDesktopWidth`,
   * because a full-window takeover being top of the stack is correct on a phone too,
   * and one code path cannot disagree with itself across a breakpoint. The narrower
   * fix is deliberate: raising ALL drawers would put a side panel above any dialog
   * unconditionally, which is wrong the moment a dialog is opened from INSIDE a side
   * panel. Nothing does that today, so the general hazard is noted rather than
   * pre-solved — if you add a `Dialog`-presenting sheet inside an `anchor='right'`
   * panel, this is the line to revisit.
   */
  const paperSxProp = resolvedPaperSx
    ? ({
        '& .MuiPaper-root': resolvedPaperSx,
        ...(fullScreen ? { zIndex: theme.zIndex.modal + 1 } : null),
      } as SxProps<Theme>)
    : undefined;

  /*
   * VERA-315 — the desktop branch. Same content, centred.
   *
   * `overflowY: 'auto'` on the Paper (Dialog's own default) is load-bearing, not
   * incidental: the header block above is `position: sticky`, and sticky only works
   * against the nearest SCROLLING ancestor. Locking the Paper to `overflow: hidden`
   * for a tidier radius would clip long content with nothing able to scroll it — the
   * exact failure `fullScreen` drawers were given a descendant scroll region to avoid.
   */
  if (asDialog) {
    return (
      <Dialog
        open={openDrawer}
        onClose={handleDrawerClose}
        maxWidth={false}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        PaperProps={{
          ref: animatedPaperRef,
          sx: {
            width: 'min(520px, calc(100vw - 64px))',
            m: 0,
            borderRadius: `${theme.shape.borderRadius}px`,
            maxHeight: 'min(80vh, 720px)',
            overflowY: 'auto',
            backgroundColor: backgroundColor ?? theme.palette.common.white,
            ...(paperSx as object),
          },
        }}
      >
        {drawerContent}
      </Dialog>
    );
  }

  // Use regular Drawer when swipe to close is disabled
  if (disableSwipeToClose) {
    return (
      <Drawer
        anchor={anchor}
        open={openDrawer}
        onClose={handleDrawerClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
        sx={paperSxProp}
        PaperProps={{ ref: animatedPaperRef }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Use SwipeableDrawer for normal behavior
  return (
    <SwipeableDrawer
      anchor={anchor}
      open={openDrawer}
      onOpen={() => setOpenDrawer(true)}
      onClose={handleDrawerClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
      sx={paperSxProp}
      PaperProps={{ ref: animatedPaperRef }}
    >
      {drawerContent}
    </SwipeableDrawer>
  );
};
